export interface Theme {
  id: string;
  name: string;
  icon: string;
  category: string;
  words: string[];
  sentences?: string[];
  wordExamples?: Record<string, string>;
  description?: string;
}

export const CATEGORIES = [
  { id: 'travel', label: 'Travel & Adventure', icon: '✈️' },
  { id: 'daily_life', label: 'Daily Life', icon: '🏠' },
  { id: 'work', label: 'Work & Business', icon: '💼' },
  { id: 'health', label: 'Health & Wellness', icon: '🏥' },
  { id: 'technology', label: 'Technology', icon: '💻' },
  { id: 'society', label: 'Society & Culture', icon: '🏛️' },
  { id: 'arts', label: 'Arts & Entertainment', icon: '🎨' },
  { id: 'basics', label: 'Basics', icon: '🔢' }
];

export const themes: Theme[] = [

  /* ===================== TRAVEL ===================== */
  
  {
    id: 'travel-1',
    name: 'Travel I (Airport & Flights)',
    description: 'Vocabulário essencial para navegar em aeroportos e voos com confiança.',
    category: 'travel',
    icon: '✈️',
    words: [
      'airport','passport','luggage','ticket','boarding','customs','departure',
      'arrival','itinerary','visa','airline','baggage','flight','gate','terminal',
      'security','check-in','carry-on','delay','cancelled','boarding pass',
      'immigration','connection','layover'
    ],
    sentences: [
      "I have my passport ready.",
      "My flight departs from terminal three.",
      "I prefer a window seat.",
      "The luggage is very heavy.",
      "We printed our boarding passes.",
      "The connection time is short.",
      "Where is the boarding gate?",
      "Can I see your passport please?",
      "My flight is delayed.",
      "How many bags are you checking in?",
      "Where is baggage claim?",
      "I have a connecting flight.",
      "What time is boarding?",
      "We need to go through customs.",
      "I prefer a window seat.",
      "What is the purpose of your visit?",
      "How long will you be staying in the country?",
      "Where will you be staying?",
      "Do you have anything to declare?",
      "Are you traveling alone or with family?"
    ],
    wordExamples: {
      "terminal": "Our flight departs from terminal three."
    }
  },
  {
    id: 'travel-2',
    name: 'Travel II (Accommodation & Tourism)',
    description: 'Termos práticos para lidar com acomodações e situações turísticas em viagens.',
    category: 'travel',
    icon: '🧳',
    words: [
      'hotel','hostel','resort','booking','reservation','tour','tourist',
      'guide','guidebook','map','souvenir','landmark','museum','beach','city',
      'attraction','adventure','trip','journey','vacation','holiday','cruise'
    ],
    sentences: [
      "I booked a room for three nights.",
      "The museum is very famous.",
      "We bought some souvenirs.",
      "The tour guide was very helpful.",
      "I have a map of the city.",
      "The beach is beautiful today.",
      "I'd like to book a room.",
      "Where is the nearest museum?",
      "Can you recommend a good tour?",
      "How much is the ticket?",
      "Is breakfast included?",
      "We are looking for the beach.",
      "I need a map of the city.",
      "Do you have any available rooms?"
]
  },
  
  /* ===================== RESTAURANT ===================== */
  
  {
    id: 'restaurant-1',
    name: 'Restaurant I (Ordering)',
    description: 'Como pedir comida e se comunicar com garçons em um restaurante.',
    category: 'daily_life',
    icon: '🍽️',
    words: [
      'menu','waiter','waitress','order','bill','tip','dish','appetizer',
      'main course','dessert','beverage','chef','special','recommendation',
      'ingredients','portion','spicy','sweet','salty'
    ],
    sentences: [
      "I would like the chef's special.",
      "This dish is quite spicy.",
      "We will pay the bill together.",
      "The appetizer was delicious.",
      "I am ready to order.",
      "This is my favorite beverage.",
      "Can I see the menu, please?",
      "I would like to order now.",
      "What is the chef's special?",
      "Can I have the bill, please?",
      "This dish is very spicy.",
      "Do you have any vegetarian options?",
      "I'll have a glass of water.",
      "Keep the change."
]
  },
  {
    id: 'restaurant-2',
    name: 'Restaurant II (Dining Experience)',
    description: 'Aprofundando a experiência de jantar fora e etiqueta em restaurantes.',
    category: 'daily_life',
    icon: '🍷',
    words: [
      'reservation','table','fork','knife','spoon','napkin','plate','glass',
      'buffet','cuisine','tableware','utensils','service','complaint',
      'review','rating','atmosphere','crowded','quiet'
    ],
    sentences: [
      "I made a reservation for two.",
      "The atmosphere is very quiet.",
      "The service was excellent today.",
      "This table has a great view.",
      "I will leave a good review.",
      "The restaurant is quite crowded.",
      "I have a reservation for two.",
      "Could we get another fork?",
      "The atmosphere here is great.",
      "Is this table available?",
      "I'd like to leave a review.",
      "The service was excellent.",
      "Can we sit by the window?",
      "How long is the wait?"
]
  },
  
  /* ===================== HOTEL ===================== */
  
  {
    id: 'hotel-1',
    name: 'Hotel I',
    description: 'Frases e palavras chave para check-in, check-out e serviços de hotel.',
    category: 'travel',
    icon: '🏨',
    words: [
      'reception','room','suite','lobby','concierge','amenities','key','card',
      'check-in','check-out','housekeeping','laundry','vacancy','deposit',
      'cleaning','view','balcony','elevator','floor','noise'
    ],
    sentences: [
      "I left my key at the reception.",
      "The room is very clean.",
      "We requested extra towels.",
      "The elevator is working fine.",
      "I paid the deposit yesterday.",
      "Our suite has a nice balcony.",
      "What time is check-out?",
      "Can I get an extra key card?",
      "Please clean the room.",
      "Is there a deposit required?",
      "The elevator is out of order.",
      "We need more towels.",
      "Where is the dining room?",
      "Can I get a wake-up call?"
]
  },
  {
    id: 'hotel-2',
    name: 'Hotel II (Services & Issues)',
    description: 'Lidando com problemas, serviços adicionais e comunicação no hotel.',
    category: 'travel',
    icon: '🛎️',
    words: [
      'problem','complaint','towel','pillow','blanket','air conditioning',
      'heater','broken','noisy','dirty','manager','bill','charge',
      'room service','pool','gym','breakfast','password','wifi','safe'
    ],
    sentences: [
      "The air conditioning is not working.",
      "My room is too noisy.",
      "Could I have an extra pillow, please?",
      "The wifi password doesn't work.",
      "What time is breakfast served?",
      "Is the pool open today?",
      "I would like to order room service.",
      "There is a mistake on my bill.",
      "I need to speak with the manager.",
      "Could you send someone to fix the heater?",
      "We need more blankets.",
      "How do I use the safe?",
      "Where is the gym located?",
      "The room is dirty."
]
  },
  
  /* ===================== TRANSPORT ===================== */
  
  {
    id: 'transportation-1',
    name: 'Transportation I (Public)',
    description: 'Vocabulário útil para usar ônibus, trens e o transporte público.',
    category: 'travel',
    icon: '🚆',
    words: [
      'bus','train','subway','tram','station','platform','route','schedule',
      'fare','ticket','passenger','transfer','delay','arrival','departure'
    ],
    sentences: [
      "The bus arrives in five minutes.",
      "I bought a transfer ticket.",
      "The subway is very fast.",
      "We waited at the platform.",
      "The schedule is very strict.",
      "This is the correct route.",
      "When does the next bus arrive?",
      "Where is the nearest subway station?",
      "How much is the fare?",
      "The train is delayed.",
      "Which platform for the express train?",
      "Do I need a transfer ticket?",
      "Please step back from the platform doors.",
      "This is my stop."
]
  },
  {
    id: 'transportation-2',
    name: 'Transportation II (Private)',
    description: 'Termos relacionados a táxis, aluguel de carros e transporte privado.',
    category: 'travel',
    icon: '🚗',
    words: [
      'taxi','driver','car','rental','license','insurance','traffic','parking',
      'fuel','engine','speed','map','gps','accident','repair','tire','brake'
    ],
    sentences: [
      "I rented a car for the weekend.",
      "The traffic is heavy today.",
      "I have my driver's license here.",
      "The gas station is nearby.",
      "I parked the car in the garage.",
      "The GPS is turned on.",
      "I need to rent a car.",
      "My car broke down.",
      "Where is the nearest gas station?",
      "The traffic is very heavy.",
      "Do you have GPS?",
      "I lost my driver's license.",
      "How much is parking?",
      "Please slow down."
]
  },
  
  /* ===================== SHOPPING ===================== */
  
  {
    id: 'shopping-1',
    name: 'Shopping I (Basics)',
    description: 'O básico de compras, preços e pagamento em lojas.',
    category: 'daily_life',
    icon: '🛍️',
    words: [
      'store','shop','price','discount','sale','cashier','receipt','customer',
      'purchase','bag','checkout','refund','exchange','product','brand'
    ],
    sentences: [
      "I want to purchase this item.",
      "The discount is very good.",
      "I kept the receipt.",
      "The cashier was very polite.",
      "These products are on sale.",
      "I paid with my credit card.",
      "How much does this cost?",
      "Do you offer a discount?",
      "I would like a refund.",
      "Can I pay by credit card?",
      "Where is the cashier?",
      "Can I get a receipt?",
      "This is on sale.",
      "I'm just browsing."
]
  },
  {
    id: 'shopping-2',
    name: 'Shopping II (Clothes & Goods)',
    description: 'Comprando roupas, tamanhos, e produtos diversos no shopping.',
    category: 'daily_life',
    icon: '👕',
    words: [
      'size','fitting room','style','color','material','quality','cheap',
      'expensive','fashion','trend','shirt','pants','shoes','jacket','hat',
      'jewelry','watch','electronics'
    ],
    sentences: [
      "This shirt fits me perfectly.",
      "The jacket is very warm.",
      "I like the color of these pants.",
      "The quality is excellent.",
      "These shoes are comfortable.",
      "The material is very soft.",
      "Do you have this in a different color?",
      "Where is the fitting room?",
      "This jacket is too expensive.",
      "What size are these shoes?",
      "I'm looking for a trendy shirt.",
      "Is this made of cotton?",
      "I need a smaller size.",
      "It fits perfectly."
]
  },
  
  /* ===================== HEALTH ===================== */
  
  {
    id: 'health-1',
    name: 'Health I (Doctor Visit)',
    description: 'Como descrever sintomas e se comunicar em uma visita médica.',
    category: 'health',
    icon: '🏥',
    words: [
      'doctor','hospital','appointment','symptom','treatment','medicine',
      'prescription','diagnosis','nurse','clinic','emergency','pain','fever',
      'injury','infection','blood pressure','temperature'
    ],
    sentences: [
      "I took the medicine this morning.",
      "The doctor gave me a prescription.",
      "My blood pressure is normal.",
      "The clinic is open today.",
      "I feel much better now.",
      "The treatment was successful.",
      "I have an appointment with the doctor.",
      "My head hurts a lot.",
      "I need a prescription for this medicine.",
      "Where is the emergency room?",
      "I have a high fever.",
      "My blood pressure is normal.",
      "I feel dizzy.",
      "Does it hurt when I press here?"
]
  },
  {
    id: 'health-2',
    name: 'Health II (Wellness)',
    description: 'Bem-estar, farmácia, nutrição e cuidados pessoais de saúde.',
    category: 'health',
    icon: '💊',
    words: [
      'exercise','diet','nutrition','sleep','stress','mental health','therapy',
      'vitamins','hydration','yoga','meditation','fitness','healthy','habit'
    ],
    sentences: [
      "I exercise every morning.",
      "Drinking water is important.",
      "I try to eat a healthy diet.",
      "Yoga helps me relax.",
      "I sleep eight hours a night.",
      "Taking vitamins is good for you.",
      "I try to exercise every day.",
      "Drinking water is important for hydration.",
      "I do yoga for my mental health.",
      "Lack of sleep causes stress.",
      "I want to start a healthy diet.",
      "Do you take any vitamins?",
      "Meditation helps me relax.",
      "It's important to build good habits."
]
  },
  
  /* ===================== EDUCATION ===================== */
  
  {
    id: 'education-1',
    name: 'Education I',
    description: 'Vocabulário do ambiente escolar, universidade e estudos.',
    category: 'technology', // Grouped with tech or society usually, but let's say Technology for now or add Education category? I used basics in my thought but let's stick to Technology as "Education & Tech" maybe? Or just Basics.
    // Actually, I defined 8 categories. Let's put Education in Basics or Technology.
    // Let's put it in Basics for now as it's foundational.
    icon: '🎓',
    words: [
      'school','university','student','teacher','classroom','lesson','homework',
      'exam','degree','lecture','graduate','textbook','subject','curriculum',
      'scholarship'
    ],
    sentences: [
      "I finished all my homework.",
      "The lecture was very interesting.",
      "She passed the final exam.",
      "The university campus is large.",
      "I bought a new textbook.",
      "The students are studying hard.",
      "I have a lot of homework today.",
      "When is the final exam?",
      "She is a university student.",
      "The teacher gave a great lecture.",
      "I need to buy a new textbook.",
      "He got a scholarship for his degree.",
      "What subject do you like most?",
      "Class is over."
]
  },
  
  /* ===================== TECHNOLOGY ===================== */
  
  {
    id: 'technology-1',
    name: 'Technology I',
    description: 'Termos do dia a dia sobre computadores, internet e dispositivos.',
    category: 'technology',
    icon: '💻',
    words: [
      'computer','internet','software','hardware','smartphone','application',
      'download','upload','password','keyboard','monitor','mouse','network',
      'database','code','server','cloud','wifi','bug','update'
    ],
    sentences: [
      "I downloaded the new application.",
      "My computer is working perfectly.",
      "I saved the file in the cloud.",
      "The wifi connection is strong.",
      "I updated the software yesterday.",
      "The database is secure.",
      "I forgot my password.",
      "My computer is running slow.",
      "Please connect to the wifi.",
      "The software needs an update.",
      "I'm downloading an application.",
      "Can you send the file to the cloud?",
      "My keyboard is broken.",
      "Please restart the server."
]
  },
  
  /* ===================== WORK ===================== */
  
  {
    id: 'work-1',
    name: 'Work I',
    description: 'Comunicação básica no ambiente de trabalho e vida profissional.',
    category: 'work',
    icon: '💼',
    words: [
      'office','colleague','manager','meeting','deadline','project',
      'presentation','salary','interview','resume','promotion','contract',
      'benefits','schedule','task','email','report','team','client'
    ],
    sentences: [
      "I finished the project on time.",
      "The meeting was very productive.",
      "My manager approved the report.",
      "We signed the new contract.",
      "I received my salary today.",
      "Our team worked very hard.",
      "We have a meeting at 10 AM.",
      "When is the deadline for this project?",
      "I need to send an email to the client.",
      "She got a promotion last week.",
      "My salary is paid monthly.",
      "The manager approved the report.",
      "I have a job interview tomorrow.",
      "Our team is working hard."
]
  },
  
  /* ===================== DAILY LIFE ===================== */
  
  {
    id: 'daily-life',
    name: 'Daily Life',
    description: 'Palavras e frases da rotina e atividades do dia a dia.',
    category: 'daily_life',
    icon: '🏠',
    words: [
      'wake up','sleep','eat','drink','cook','clean','shopping','exercise',
      'relax','shower','work','study','commute','rest','routine','habit'
    ],
    sentences: [
      "I wake up early every day.",
      "I clean the house on weekends.",
      "I cooked dinner for my family.",
      "I enjoy a quiet routine.",
      "I usually rest in the afternoon.",
      "It is time to go to work.",
      "I wake up early every morning.",
      "It's time to cook dinner.",
      "I need to clean the house.",
      "What do you do to relax?",
      "I commute to work by train.",
      "I usually study in the evening.",
      "Don't forget to take a shower.",
      "It's important to rest."
]
  },
  
  /* ===================== FAMILY ===================== */
  
  {
    id: 'family',
    name: 'Family',
    description: 'Membros da família, relacionamentos e convivência.',
    category: 'daily_life',
    icon: '👪',
    words: [
      'mother','father','brother','sister','sibling','grandmother','grandfather',
      'uncle','aunt','cousin','nephew','niece','relative','parents','children',
      'marriage','wedding','divorce'
    ],
    sentences: [
      "My brother is older than me.",
      "We visited our grandparents.",
      "My parents live in this city.",
      "The family gathered for dinner.",
      "I have two younger siblings.",
      "My cousin is getting married.",
      "My brother is older than me.",
      "We are visiting our grandparents.",
      "She is my cousin.",
      "They are planning a wedding.",
      "My parents live in another city.",
      "He is my favorite uncle.",
      "I have three siblings.",
      "The whole family is gathering today."
]
  },
  
  /* ===================== EMOTIONS ===================== */
  
  {
    id: 'emotions',
    name: 'Emotions',
    description: 'Como expressar sentimentos, emoções e estados de espírito.',
    category: 'basics',
    icon: '😊',
    words: [
      'happy','sad','angry','excited','nervous','bored','confused','proud',
      'scared','surprised','disappointed','frustrated','jealous','anxious',
      'grateful','lonely','calm','tired'
    ],
    sentences: [
      "I feel very happy today.",
      "She was excited about the news.",
      "I am proud of my achievements.",
      "He remained calm during the test.",
      "I am grateful for your help.",
      "They felt tired after the trip.",
      "I feel very happy today.",
      "He was angry about the mistake.",
      "I'm so excited for the trip.",
      "She looked surprised.",
      "Don't be afraid.",
      "I am proud of your work.",
      "This situation makes me nervous.",
      "Why are you so sad?"
]
  },
  
  /* ===================== TIME ===================== */
  
  {
    id: 'time',
    name: 'Time',
    description: 'Falando sobre horas, dias, meses e conceitos de tempo.',
    category: 'basics',
    icon: '⏰',
    words: [
      'second','minute','hour','day','week','month','year','today','yesterday',
      'tomorrow','calendar','schedule','appointment','deadline','timeline',
      'early','late','on time','delay'
    ],
    sentences: [
      "I arrived exactly on time.",
      "The deadline is next week.",
      "I marked the date on my calendar.",
      "We finished the task early.",
      "The schedule is very tight.",
      "I will see you tomorrow.",
      "What time is it?",
      "I'll see you tomorrow.",
      "The meeting is next week.",
      "You are early.",
      "Don't be late.",
      "It will take an hour.",
      "I have an appointment at noon.",
      "The deadline is on Friday."
]
  },
  
  /* ===================== FOOD ===================== */
  
  {
    id: 'food-drinks',
    name: 'Food & Drinks',
    description: 'Vocabulário geral sobre alimentos, ingredientes e bebidas.',
    category: 'daily_life',
    icon: '🍎',
    words: [
      'bread','rice','pasta','meat','chicken','fish','vegetable','fruit',
      'breakfast','lunch','dinner','snack','water','coffee','tea','juice',
      'beer','wine','sugar','salt','oil'
    ],
    sentences: [
      "The chicken is perfectly cooked.",
      "I drink coffee every morning.",
      "This fruit is very sweet.",
      "I prefer to drink water.",
      "The bread is fresh today.",
      "I enjoy eating pasta.",
      "I would like some water.",
      "This chicken is delicious.",
      "Do you want coffee or tea?",
      "I prefer red wine.",
      "Pass the salt, please.",
      "I usually eat bread for breakfast.",
      "This fruit is very sweet.",
      "Can I have some more rice?"
]
  },
  
  /* ===================== WEATHER ===================== */
  
  {
    id: 'weather',
    name: 'Weather',
    description: 'Como descrever o clima, temperatura e estações do ano.',
    category: 'daily_life',
    icon: '🌦️',
    words: [
      'sunny','rainy','cloudy','storm','snow','wind','hot','cold','warm',
      'cool','forecast','temperature','humidity','season','spring','summer',
      'autumn','winter'
    ],
    sentences: [
      "The sun is shining today.",
      "It rained heavily last night.",
      "The temperature is very pleasant.",
      "I love the autumn season.",
      "The wind is blowing softly.",
      "We expect snow tomorrow.",
      "It's very sunny today.",
      "Looks like it's going to rain.",
      "The wind is quite strong.",
      "I love the summer season.",
      "It's too hot outside.",
      "The forecast says snow.",
      "Bring a jacket, it's cold.",
      "The humidity is very high."
]
  },
  
  /* ===================== ENTERTAINMENT ===================== */
  
  {
    id: 'entertainment',
    name: 'Entertainment',
    description: 'Filmes, séries, atividades de lazer e entretenimento.',
    category: 'arts',
    icon: '🎬',
    words: [
      'movie','music','concert','theater','actor','actress','director','song',
      'album','band','festival','ticket','show','comedy','drama','game','tv'
    ],
    sentences: [
      "The movie was very funny.",
      "I listen to music every day.",
      "We enjoyed the comedy show.",
      "The actor played his role well.",
      "I bought tickets for the concert.",
      "The new game is exciting.",
      "Let's watch a movie tonight.",
      "I bought tickets for the concert.",
      "Who is your favorite actor?",
      "This game is fun.",
      "The band played a new song.",
      "I love going to the theater.",
      "This comedy show is hilarious.",
      "What's on TV?"
]
  },
  
  /* ===================== COMMUNICATION ===================== */
  
  {
    id: 'communication',
    name: 'Communication',
    description: 'Verbos e expressões para se comunicar melhor e manter conversas.',
    category: 'society',
    icon: '📱',
    words: [
      'call','message','email','chat','voice','video','contact','number',
      'signal','connection','mute','speaker','reply','forward','delete','block'
    ],
    sentences: [
      "I sent you an email.",
      "I received your message.",
      "The video call was clear.",
      "I saved your phone number.",
      "I will forward the information.",
      "The internet signal is good.",
      "I will call you later.",
      "Did you get my message?",
      "Please send me an email.",
      "The connection is bad.",
      "Turn on your video.",
      "Your microphone is on mute.",
      "I can't hear the speaker.",
      "Don't forget to reply."
]
  },

  /* ===================== MONEY & BANKING ===================== */
{
  id: 'money-banking',
  name: 'Money & Banking',
    description: 'Vocabulário financeiro, bancos, cartões e dinheiro.',
  category: 'society',
  icon: '💰',
  words: [
    'bank','account','credit card','debit card','loan','interest','savings',
    'withdraw','deposit','transfer','balance','currency','exchange rate',
    'ATM','cash','payment','online banking','wallet'
  ],
    sentences: [
      "I opened a new savings account.",
      "The exchange rate is favorable.",
      "I paid using online banking.",
      "I withdrew cash from the ATM.",
      "The loan was approved.",
      "I checked my account balance.",
      "I want to open a bank account.",
      "Can I pay with a credit card?",
      "What is the exchange rate?",
      "I need to withdraw some cash.",
      "My balance is low.",
      "Where is the nearest ATM?",
      "I'd like to transfer some money.",
      "Do you offer online banking?"
]
},

/* ===================== POLITICS & GOVERNMENT ===================== */
{
  id: 'government',
  name: 'Government',
    description: 'Termos básicos sobre governo, política e estado.',
  category: 'society',
  icon: '🏛️',
  words: [
    'government','law','rule','policy','election','vote','president',
    'minister','citizen','rights','passport','visa','border','tax',
    'public service'
  ],
    sentences: [
      "The president gave a speech.",
      "The new law was passed.",
      "I renewed my passport.",
      "Citizens pay taxes every year.",
      "The public service is efficient.",
      "The election results are clear.",
      "Who is the president?",
      "The new law was passed.",
      "I need to renew my passport.",
      "Everyone has the right to vote.",
      "The border is closed.",
      "We pay taxes every year.",
      "It's a public service.",
      "The election is next month."
]
},

/* ===================== LAW & SECURITY ===================== */
{
  id: 'law-security',
  name: 'Law & Security',
    description: 'Polícia, regras, leis e segurança pública.',
  category: 'society',
  icon: '⚖️',
  words: [
    'police','crime','lawyer','court','judge','trial','evidence','arrest',
    'fine','punishment','prison','security','guard','investigation'
  ],
    sentences: [
      "The police caught the thief.",
      "The lawyer defended his client.",
      "The judge made a fair decision.",
      "The security guard is on duty.",
      "The evidence is very strong.",
      "The trial ended yesterday.",
      "Call the police immediately.",
      "He is a famous lawyer.",
      "The judge made a decision.",
      "There is no evidence.",
      "The trial starts tomorrow.",
      "He was arrested for the crime.",
      "The security guard is at the door.",
      "They are doing an investigation."
]
},

/* ===================== HOUSE & HOME ===================== */
{
  id: 'house-home',
  name: 'House & Home',
    description: 'Cômodos da casa, móveis e vida doméstica.',
  category: 'daily_life',
  icon: '🏠',
  words: [
    'house','apartment','rent','mortgage','kitchen','bathroom','bedroom',
    'living room','furniture','sofa','bed','table','chair','window','door',
    'garden','garage','balcony','repair'
  ],
    sentences: [
      "My apartment is very cozy.",
      "I bought new furniture.",
      "The kitchen is quite large.",
      "I painted the bedroom walls.",
      "We relaxed in the living room.",
      "I parked in the garage.",
      "I'm looking for an apartment to rent.",
      "The kitchen is very spacious.",
      "Please close the window.",
      "We bought new furniture.",
      "The living room is my favorite.",
      "I need to repair the roof.",
      "Let's sit on the balcony.",
      "The garage is full."
]
},

/* ===================== CLEANING & CHORES ===================== */
{
  id: 'cleaning-chores',
  name: 'Cleaning & Chores',
    description: 'Tarefas de casa, limpeza e manutenção.',
  category: 'daily_life',
  icon: '🧹',
  words: [
    'clean','wash','laundry','dry','vacuum','dust','mop','trash','garbage',
    'dishwasher','detergent','soap','tidy','organize','fold','iron'
  ],
    sentences: [
      "I washed all the dishes.",
      "I folded the clean laundry.",
      "The floor is completely mopped.",
      "I organized my closet.",
      "I took out the garbage.",
      "The house looks very tidy.",
      "I need to do the laundry.",
      "Please take out the trash.",
      "The floor needs to be mopped.",
      "Have you washed the dishes?",
      "I have to iron my shirt.",
      "We need more detergent.",
      "Let's tidy up the room.",
      "The house is dusty."
]
},

/* ===================== CLOTHING ===================== */
{
  id: 'clothing',
  name: 'Clothing',
    description: 'Peças de roupa, acessórios e calçados.',
  category: 'daily_life',
  icon: '👗',
  words: [
    'shirt','pants','dress','skirt','jacket','coat','sweater','jeans',
    'shoes','boots','sandals','socks','hat','cap','scarf','gloves',
    'fashion','outfit'
  ],
    sentences: [
      "I am wearing a warm coat.",
      "These shoes fit perfectly.",
      "I bought a new winter hat.",
      "The dress is very elegant.",
      "I need a thick scarf.",
      "These pants are very comfortable.",
      "This shirt looks great on you.",
      "I need a new pair of shoes.",
      "Where can I buy a winter coat?",
      "These pants are too tight.",
      "Don't forget your gloves.",
      "I love your dress.",
      "What is the latest fashion trend?",
      "That hat suits you."
]
},

/* ===================== BODY PARTS ===================== */
{
  id: 'body-parts',
  name: 'Body Parts',
    description: 'As principais partes do corpo humano e anatomia básica.',
  category: 'health',
  icon: '🧍',
  words: [
    'head','hair','face','eye','ear','nose','mouth','tooth','neck','shoulder',
    'arm','hand','finger','leg','knee','foot','toe','heart','stomach'
  ],
    sentences: [
      "My hands are cold.",
      "He has strong arms.",
      "Her hair is very long.",
      "I closed my eyes.",
      "He hurt his knee yesterday.",
      "I took a deep breath.",
      "My head hurts.",
      "He has blue eyes.",
      "Raise your right hand.",
      "My legs are tired.",
      "Open your mouth.",
      "She hurt her shoulder.",
      "I have a pain in my stomach.",
      "Watch your step."
]
},

/* ===================== ANIMALS ===================== */
{
  id: 'animals',
  name: 'Animals',
    description: 'Nomes de animais domésticos e selvagens comuns.',
  category: 'basics',
  icon: '🐾',
  words: [
    'dog','cat','bird','fish','horse','cow','sheep','goat','pig','lion',
    'tiger','elephant','bear','monkey','rabbit','pet','wild'
  ],
    sentences: [
      "The dog barked loudly.",
      "The cat is sleeping on the sofa.",
      "We saw birds in the park.",
      "The horses run very fast.",
      "The elephant is a large animal.",
      "I have a pet fish.",
      "I have a pet dog.",
      "The cat is sleeping.",
      "We saw a lion at the zoo.",
      "The bird is singing.",
      "Cows eat grass.",
      "He is afraid of snakes.",
      "Look at that little rabbit.",
      "The elephant is very big."
]
},

/* ===================== COLORS ===================== */
{
  id: 'colors',
  name: 'Colors',
    description: 'As cores, tons e descrições visuais.',
  category: 'basics',
  icon: '🎨',
  words: [
    'red','blue','green','yellow','black','white','orange','purple','pink',
    'brown','gray','gold','silver','dark','light','bright'
  ],
    sentences: [
      "The sky is clear blue.",
      "I like the red paint.",
      "The grass is bright green.",
      "She wore a yellow dress.",
      "The night is very dark.",
      "This is a light gray color.",
      "The sky is blue.",
      "She is wearing a red dress.",
      "Grass is green.",
      "I prefer dark colors.",
      "The walls are painted white.",
      "He has brown hair.",
      "Yellow is a bright color.",
      "The car is silver."
]
},

/* ===================== NUMBERS ===================== */
{
  id: 'numbers',
  name: 'Numbers',
    description: 'Números, contagem e quantidades.',
  category: 'basics',
  icon: '🔢',
  words: [
    'zero','one','two','three','four','five','six','seven','eight','nine',
    'ten','hundred','thousand','first','second','third','count','number'
  ],
    sentences: [
      "I counted ten cars.",
      "This is my first time here.",
      "He won the first prize.",
      "I have three apples.",
      "We need two more chairs.",
      "A hundred people came.",
      "There are ten people here.",
      "I am the first in line.",
      "It costs one hundred dollars.",
      "Can you count to twenty?",
      "He won third place.",
      "I have two brothers.",
      "Give me five minutes.",
      "The number is zero."
]
},

/* ===================== DIRECTIONS ===================== */
{
  id: 'directions',
  name: 'Directions',
    description: 'Como pedir informações, dar direções e se localizar.',
  category: 'travel',
  icon: '🧭',
  words: [
    'left','right','straight','north','south','east','west','turn',
    'corner','intersection','across','behind','near','far','next to'
  ],
    sentences: [
      "I went straight ahead.",
      "The bank is on the left.",
      "I walked towards the north.",
      "The store is very near.",
      "It is right behind the building.",
      "I turned at the corner.",
      "Turn left at the next corner.",
      "Go straight ahead.",
      "It's on your right.",
      "The bank is across the street.",
      "Walk towards the north.",
      "The station is near here.",
      "It's behind the building.",
      "At the intersection, turn south."
]
},

/* ===================== HOBBIES ===================== */
{
  id: 'hobbies',
  name: 'Hobbies',
    description: 'Passatempos, interesses e atividades de tempo livre.',
  category: 'arts',
  icon: '🎯',
  words: [
    'reading','writing','painting','drawing','photography','gaming',
    'cooking','baking','traveling','hiking','swimming','cycling','music',
    'dancing','singing','craft'
  ],
    sentences: [
      "I read books in my free time.",
      "She paints beautiful pictures.",
      "We go cycling on weekends.",
      "He loves to cook dinner.",
      "I practice playing the guitar.",
      "Photography is my passion.",
      "I enjoy reading books.",
      "She loves painting.",
      "My hobby is photography.",
      "Do you like cooking?",
      "He plays video games all day.",
      "We go hiking on weekends.",
      "Swimming is good exercise.",
      "I'm taking dancing lessons."
]
},

/* ===================== RELIGION ===================== */
{
  id: 'religion',
  name: 'Religion',
    description: 'Vocabulário básico relacionado a fé e religião.',
  category: 'society',
  icon: '⛪',
  words: [
    'church','mosque','temple','prayer','faith','god','religion','belief',
    'festival','holiday','ritual','spiritual'
  ],
    sentences: [
      "The church is very old.",
      "They celebrated the festival.",
      "It was a spiritual journey.",
      "The temple is peaceful.",
      "They respect their traditions.",
      "The holiday brings joy.",
      "He goes to church every Sunday.",
      "The mosque is beautiful.",
      "It's a spiritual journey.",
      "They have strong faith.",
      "We celebrate this festival every year.",
      "The temple is very old.",
      "It's a religious holiday.",
      "They practice their beliefs."
]
},

/* ===================== SCIENCE ===================== */
{
  id: 'science',
  name: 'Science',
    description: 'Termos comuns do mundo científico e pesquisas.',
  category: 'technology', // Grouping with Tech/Education
  icon: '🔬',
  words: [
    'experiment','research','theory','hypothesis','laboratory','scientist',
    'biology','chemistry','physics','energy','atom','cell','data','analysis'
  ],
    sentences: [
      "The experiment was a success.",
      "Scientists analyzed the data.",
      "The theory is well known.",
      "We studied human cells.",
      "Physics explains natural laws.",
      "The laboratory is fully equipped.",
      "The scientist conducted an experiment.",
      "This is a new theory.",
      "The laboratory is well equipped.",
      "Biology is the study of life.",
      "Energy cannot be destroyed.",
      "We need to analyze the data.",
      "It's a chemical reaction.",
      "Physics explains how the universe works."
]
},

/* ===================== SPACE ===================== */
{
  id: 'space',
  name: 'Space',
    description: 'O universo, planetas e exploração espacial.',
  category: 'technology',
  icon: '🚀',
  words: [
    'planet','star','moon','sun','galaxy','universe','astronaut','rocket',
    'satellite','orbit','spacecraft','telescope'
  ],
    sentences: [
      "The stars shine at night.",
      "The spacecraft reached orbit.",
      "The universe is vast.",
      "We saw the full moon.",
      "The satellite sends signals.",
      "Astronauts explore space.",
      "The moon orbits the earth.",
      "Look at the stars tonight.",
      "The sun is very hot.",
      "Astronauts travel in spacecraft.",
      "Our galaxy is the Milky Way.",
      "We launched a new satellite.",
      "The universe is expanding.",
      "Mars is the red planet."
]
},

/* ===================== AGRICULTURE ===================== */
{
  id: 'agriculture',
  name: 'Agriculture',
    description: 'Fazenda, plantações e agricultura.',
  category: 'work',
  icon: '🌾',
  words: [
    'farm','farmer','crop','harvest','soil','seed','plant','irrigation',
    'tractor','field','organic','fertilizer'
  ],
    sentences: [
      "The crops grow fast.",
      "The farmer drove the tractor.",
      "The soil is rich and dark.",
      "We planted seeds today.",
      "The harvest was abundant.",
      "They use organic fertilizer.",
      "The farmer is in the field.",
      "We had a good harvest this year.",
      "The soil is very rich.",
      "They plant seeds in the spring.",
      "We need an irrigation system.",
      "The tractor is broken.",
      "They use organic fertilizer.",
      "Growing crops takes time."
]
},

/* ===================== INDUSTRY ===================== */
{
  id: 'industry',
  name: 'Industry',
    description: 'Fábricas, produção e setor industrial.',
  category: 'work',
  icon: '🏭',
  words: [
    'factory','machine','production','manufacturing','worker','assembly',
    'automation','robot','engineering','material'
  ],
    sentences: [
      "The factory produces cars.",
      "The machines run automatically.",
      "Workers assemble the parts.",
      "Production increased this year.",
      "The robot moves quickly.",
      "Safety is highly prioritized.",
      "The factory produces cars.",
      "The machine is running well.",
      "They use robots for manufacturing.",
      "The assembly line is fast.",
      "Engineering is a hard job.",
      "Automation reduces costs.",
      "The materials are imported.",
      "Safety is important for workers."
]
},

/* ===================== NEWS & MEDIA ===================== */
{
  id: 'news-media',
  name: 'News & Media',
    description: 'Jornalismo, notícias, televisão e mídia.',
  category: 'society',
  icon: '📰',
  words: [
    'news','report','journalist','article','headline','interview','press',
    'media','broadcast','channel','newspaper','magazine','website'
  ],
    sentences: [
      "I read the morning newspaper.",
      "The journalist wrote an article.",
      "The news spread quickly.",
      "They broadcast the interview.",
      "The headline was interesting.",
      "I checked the news website.",
      "Did you read the news today?",
      "The journalist wrote a great article.",
      "I saw the broadcast last night.",
      "The headline caught my attention.",
      "He was interviewed on TV.",
      "I subscribe to a magazine.",
      "The press is here.",
      "Check the news website."
]
},

/* ===================== INTERNET & SOCIAL MEDIA ===================== */
{
  id: 'social-media',
  name: 'Social Media',
    description: 'Redes sociais, internet e comunicação digital.',
  category: 'technology',
  icon: '📲',
  words: [
    'post','like','comment','share','follow','profile','story','hashtag',
    'influencer','viral','feed','timeline','upload','subscribe'
  ],
    sentences: [
      "I posted a new picture.",
      "Many people liked the video.",
      "I follow my favorite brands.",
      "The story went viral online.",
      "I left a positive comment.",
      "He shared the link.",
      "I posted a new photo.",
      "Please like and share this video.",
      "He has many followers.",
      "She is a famous influencer.",
      "Leave a comment below.",
      "The story went viral.",
      "Update your profile.",
      "Use the correct hashtag."
]
},

/* ===================== PERSONALITY ===================== */
{
  id: 'personality',
  name: 'Personality',
    description: 'Como descrever o caráter e traços de personalidade das pessoas.',
  category: 'basics',
  icon: '🧠',
  words: [
    'kind','friendly','shy','confident','honest','funny','serious','lazy',
    'hardworking','creative','patient','polite','rude'
  ],
    sentences: [
      "She is very friendly.",
      "He works very hard.",
      "I am confident in my skills.",
      "They are polite to everyone.",
      "She is incredibly creative.",
      "He is an honest person.",
      "She is a very kind person.",
      "He is too shy to speak.",
      "You are very creative.",
      "I appreciate honest people.",
      "The teacher is very patient.",
      "Don't be rude.",
      "He is a hardworking student.",
      "She always tells funny jokes."
]
},

/* ===================== CRIME & EMERGENCY ===================== */
{
  id: 'emergency',
  name: 'Emergency',
    description: 'Como pedir ajuda, vocabulário de urgência e resgate.',
  category: 'basics',
  icon: '🚨',
  words: [
    'help','emergency','danger','fire','ambulance','police','accident',
    'injury','rescue','alarm','panic','escape'
  ],
    sentences: [
      "The ambulance arrived quickly.",
      "The fire was extinguished.",
      "We called for help.",
      "Everyone evacuated safely.",
      "The alarm worked perfectly.",
      "The police secured the area.",
      "Call for help immediately!",
      "There is a fire in the building.",
      "We need an ambulance here.",
      "It was a terrible accident.",
      "Don't panic.",
      "Where is the emergency exit?",
      "The alarm is ringing.",
      "He has a serious injury."
]
},

/* ===================== FESTIVALS & HOLIDAYS ===================== */
{
  id: 'festivals',
  name: 'Festivals',
    description: 'Feriados, celebrações e datas comemorativas.',
  category: 'arts',
  icon: '🎉',
  words: [
    'festival','holiday','celebration','party','gift','tradition','custom',
    'parade','music','dance','fireworks'
  ],
    sentences: [
      "The parade was spectacular.",
      "We exchanged gifts.",
      "The fireworks were beautiful.",
      "Everyone danced to the music.",
      "We celebrated all night.",
      "The festival was a great success.",
      "We are having a party tonight.",
      "Happy holidays!",
      "I brought a gift for you.",
      "It's a traditional celebration.",
      "The fireworks are amazing.",
      "There is a parade in the street.",
      "Let's dance and listen to music.",
      "It's a special custom."
]
},

/* ===================== LOVE & RELATIONSHIPS ===================== */
{
  id: 'relationships',
  name: 'Relationships',
    description: 'Amizades, namoro e relacionamentos interpessoais.',
  category: 'basics',
  icon: '❤️',
  words: [
    'love','relationship','partner','boyfriend','girlfriend','husband',
    'wife','date','romantic','kiss','hug','breakup','engagement'
  ],
    sentences: [
      "They are deeply in love.",
      "We respect our partners.",
      "The couple got engaged.",
      "They went on a romantic date.",
      "He is a loyal friend.",
      "They share a strong bond.",
      "They are deeply in love.",
      "She is my best friend.",
      "He asked her to marry him.",
      "They went on a romantic date.",
      "We are in a serious relationship.",
      "They had a bad breakup.",
      "He is a good husband.",
      "Give me a hug."
]
},

/* ===================== CAREER & BUSINESS ===================== */
{
  id: 'business',
  name: 'Business',
    description: 'Negócios, empresas, comércio e corporações.',
  category: 'work',
  icon: '📊',
  words: [
    'company','business','market','profit','loss','startup','investment',
    'strategy','customer','client','deal','negotiation','brand','finance'
  ],
    sentences: [
      "The company made a profit.",
      "We negotiated a good deal.",
      "The strategy worked well.",
      "Customers love our product.",
      "We secured a new investment.",
      "The brand is well known.",
      "The company is growing fast.",
      "We made a huge profit.",
      "They signed a new contract.",
      "The startup got an investment.",
      "We need a better strategy.",
      "The client is very happy.",
      "They are in negotiation.",
      "Our brand is strong."
]
},

/* ===================== ART ===================== */
{
  id: 'art',
  name: 'Art',
    description: 'Artes visuais, pintura, museus e criatividade.',
  category: 'arts',
  icon: '🖼️',
  words: [
    'art','painting','sculpture','gallery','museum','artist','creative',
    'design','style','abstract','portrait','exhibition'
  ],
    sentences: [
      "The painting is beautiful.",
      "The museum has great exhibits.",
      "She is a talented artist.",
      "The design is very modern.",
      "I bought a creative sculpture.",
      "The gallery was crowded.",
      "This painting is a masterpiece.",
      "Let's go to the art gallery.",
      "The sculptor made a beautiful statue.",
      "It's an abstract design.",
      "The artist is very talented.",
      "The exhibition opens tomorrow.",
      "I love this creative style.",
      "She drew a portrait of him."
]
},

/* ===================== MUSIC ===================== */
{
  id: 'music',
  name: 'Music',
    description: 'Estilos musicais, instrumentos e o mundo da música.',
  category: 'arts',
  icon: '🎵',
  words: [
    'song','music','instrument','guitar','piano','violin','drum','melody',
    'rhythm','band','concert','singer','lyrics'
  ],
    sentences: [
      "The melody is very catchy.",
      "The band played loudly.",
      "He sang a beautiful song.",
      "The concert was amazing.",
      "I learned the lyrics.",
      "She plays the piano well.",
      "What kind of music do you like?",
      "He plays the guitar well.",
      "The band had a great concert.",
      "I love the rhythm of this song.",
      "She is a famous singer.",
      "Do you know the lyrics?",
      "The piano melody is beautiful.",
      "He bought a new drum."
]
},

/* ===================== COOKING ===================== */
{
  id: 'cooking',
  name: 'Cooking',
    description: 'Verbos de culinária, preparo de alimentos e receitas.',
  category: 'arts',
  icon: '🍳',
  words: [
    'cook','recipe','ingredient','boil','fry','bake','grill','cut','mix',
    'stir','taste','kitchen','knife','pan','pot','oven'
  ],
    sentences: [
      "I baked a chocolate cake.",
      "The soup is boiling.",
      "I chopped the vegetables.",
      "The kitchen smells great.",
      "I grilled the meat.",
      "The recipe is easy to follow.",
      "I love to cook dinner.",
      "What is the recipe for this cake?",
      "Mix all the ingredients together.",
      "Bake it in the oven for an hour.",
      "Chop the vegetables carefully.",
      "Fry the meat in a pan.",
      "The soup tastes delicious.",
      "Don't forget to stir."
]
},

/* ===================== TOOLS & HARDWARE ===================== */
{
  id: 'tools',
  name: 'Tools',
    description: 'Ferramentas de trabalho, consertos e bricolagem.',
  category: 'basics',
  icon: '🛠️',
  words: [
    'hammer','nail','screw','screwdriver','wrench','drill','toolbox',
    'repair','fix','build','measure','tape','ladder'
  ],
    sentences: [
      "I fixed the broken chair.",
      "The toolbox is full.",
      "I used a hammer and nails.",
      "The ladder is tall enough.",
      "I measured the wooden board.",
      "The screwdriver worked perfectly.",
      "Pass me the hammer, please.",
      "I need a screwdriver for this.",
      "Use the tape to measure it.",
      "He is fixing the broken chair.",
      "My toolbox is in the garage.",
      "Drill a hole here.",
      "I can build it myself.",
      "We need a taller ladder."
]
},

/* ===================== SPORTS II ===================== */
{
  id: 'sports-2',
  name: 'Sports II (Outdoor)',
    description: 'Esportes ao ar livre, aventura e atividades físicas externas.',
  category: 'arts',
  icon: '🏃',
  words: [
    'run','marathon','hike','climb','swim','surf','ski','skate','bicycle',
    'race','trail','outdoor','adventure'
  ],
    sentences: [
      "I ran a marathon last year.",
      "We hiked the mountain trail.",
      "He swam across the lake.",
      "They skied down the hill.",
      "We enjoyed the outdoor adventure.",
      "She rode her bicycle.",
      "I run every morning.",
      "They went hiking in the mountains.",
      "She is practicing for the marathon.",
      "Let's go swimming at the beach.",
      "He loves to surf.",
      "We rented bicycles today.",
      "The race starts soon.",
      "It's a great outdoor adventure."
]
},

/* ===================== TRANSPORT III ===================== */
{
  id: 'transportation-3',
  name: 'Transportation III (Sea & Air)',
    description: 'Vocabulário sobre transporte aéreo avançado e marítimo.',
  category: 'travel',
  icon: '🛳️',
  words: [
    'ship','boat','ferry','port','harbor','captain','crew','sail',
    'cruise','cargo','pilot','helicopter'
  ],
    sentences: [
      "The ship sailed across the ocean.",
      "The ferry arrived at the port.",
      "The cargo was safely loaded.",
      "The helicopter flew over the city.",
      "The captain steered the boat.",
      "We enjoyed the cruise.",
      "The ship arrives at the port.",
      "We took a ferry across the river.",
      "The captain is experienced.",
      "The helicopter landed safely.",
      "We are going on a cruise.",
      "The cargo was unloaded.",
      "The pilot flew the plane.",
      "They are sailing across the ocean."
]
},

];
